import { useEffect, useState } from 'react';
import axios from 'axios';
import { Flame, TrendingUp, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public strip: horizontal-scrolling cards showing the most-posted city→city lanes
 * over the last 7 days. Fed by GET /api/loads/hot-lanes (public endpoint).
 *
 * @param {string} theme  'light' (default, for load board) or 'dark' (for home page)
 * @param {number} days   Look-back window (default 7)
 * @param {number} limit  Max lanes to show (default 6)
 */
export const HotLanesStrip = ({ theme = 'light', days = 7, limit = 6 }) => {
  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/loads/hot-lanes`, { params: { days, limit } });
        if (alive) setLanes(res.data.lanes || []);
      } catch {
        if (alive) setLanes([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [days, limit]);

  if (loading || lanes.length === 0) return null;

  const isDark = theme === 'dark';
  const wrapCls = isDark
    ? 'bg-gradient-to-r from-zinc-900 via-zinc-950 to-zinc-900 border-y border-red-900/30'
    : 'bg-gradient-to-r from-amber-50 via-orange-50 to-red-50 border-y border-orange-200/60';
  const headerText = isDark ? 'text-orange-300' : 'text-red-700';
  const subText = isDark ? 'text-zinc-400' : 'text-zinc-500';
  const cardBg = isDark ? 'bg-zinc-900/60 border-zinc-800 hover:border-orange-500/50' : 'bg-white border-zinc-200 hover:border-red-500';
  const cardText = isDark ? 'text-zinc-100' : 'text-zinc-900';
  const stateChip = isDark ? 'bg-red-900/40 text-orange-300 border-red-800/60' : 'bg-red-50 text-red-700 border-red-200';
  const rateText = isDark ? 'text-emerald-400' : 'text-emerald-700';

  return (
    <section className={`${wrapCls} py-5 sm:py-6`} data-testid="hot-lanes-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4">
          <div className={`inline-flex items-center justify-center w-9 h-9 ${isDark ? 'bg-red-950/60' : 'bg-red-600'} rounded-full`}>
            <Flame className={`w-4 h-4 ${isDark ? 'text-orange-400' : 'text-white'}`} strokeWidth={2.5} />
          </div>
          <div>
            <div className={`text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] ${headerText}`}>Hot Lanes This Week</div>
            <div className={`text-xs sm:text-sm font-medium ${subText}`}>Most-posted routes in the last {days} days · updated live</div>
          </div>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-0 sm:px-0 snap-x snap-mandatory sm:snap-none" data-testid="hot-lanes-scroll">
          {lanes.map((lane, i) => (
            <div
              key={`${lane.origin_city}-${lane.dest_city}-${i}`}
              className={`shrink-0 snap-start ${cardBg} border p-4 min-w-[240px] sm:min-w-[260px] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg group cursor-default`}
              data-testid={`hot-lane-${i}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-3.5 h-3.5 text-red-500" />
                <span className={`text-[10px] font-bold uppercase tracking-wider ${subText}`}>
                  {lane.count} load{lane.count === 1 ? '' : 's'}
                </span>
              </div>
              <div className={`text-sm font-bold ${cardText} leading-snug flex items-center gap-1.5 flex-wrap`}>
                <span className="truncate max-w-[100px]" title={lane.origin_city}>{lane.origin_city}</span>
                {lane.origin_state && (
                  <span className={`text-[10px] font-black px-1.5 py-0.5 border ${stateChip}`}>{lane.origin_state}</span>
                )}
                <ArrowRight className="w-3.5 h-3.5 text-red-500 shrink-0 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
                <span className="truncate max-w-[100px]" title={lane.dest_city}>{lane.dest_city}</span>
                {lane.dest_state && (
                  <span className={`text-[10px] font-black px-1.5 py-0.5 border ${stateChip}`}>{lane.dest_state}</span>
                )}
              </div>
              {lane.avg_rate ? (
                <div className={`mt-3 text-sm font-black ${rateText}`}>
                  ${Math.round(lane.avg_rate).toLocaleString()}<span className={`text-[10px] font-medium ml-1 ${subText}`}>avg</span>
                </div>
              ) : (
                <div className={`mt-3 text-[11px] italic ${subText}`}>Call for rate</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HotLanesStrip;
